import React from "react";
import { DataTableV2, Text, Pill, Intro } from "components/commons";
import locale from "localization/en";
import styles from "./order-summary-form/order-summary.module.scss";
import { prettifyDeliveryRecipient, prettifyProductType } from "utils/pretty.utils";
import { dateTimeFormat } from "utils";
import useDataTable from "hooks/useDataTable";
import { fuelColumns, nonFuelColumns } from "./order-summary-form/products/columns";
import { mapDataToListViewFuel } from "./order-summary-form/products/mapper";
import { OrderStatus, DateTime } from "enums";
import { prettifyOrderStatus } from "utils/pretty.utils";
import { formatDate } from "utils/date.utils";
import WarningAmberIcon from "@material-ui/icons/WarningTwoTone";
import ModeEditIcon from "@material-ui/icons/EditOutlined";
import { ViewRow } from "./components/view-row";
import StatusCard from "./components/status-card";

const ViewOrderSummary = ({ orderSummary, loading }) => {
  const isFuelOnly = orderSummary?.productType === "fuel";

  const { table } = useDataTable({
    value: orderSummary?.orderItems || [],
    filter: { initialState: {}, mapper: (param) => param },
    table: {
      key: "orders",
      mapper: (orders) => {
        if (isFuelOnly) {
          return mapDataToListViewFuel({
            orders,
          });
        }
      },
      columns: isFuelOnly
        ? fuelColumns.filter((c) => c.key !== "actions")
        : nonFuelColumns.filter((c) => c.key !== "actions"),
    },
  });

  const isRejected = orderSummary?.status === OrderStatus.Rejected;

  const statusCardInfo = {
    title: isRejected ? locale.rejectReason : locale.requestRevisionReason,
    date: isRejected ? orderSummary?.rejectedAt : orderSummary?.updatedAt,
    reason: orderSummary?.reason ?? "",
    remarks: orderSummary?.reasonRemarks ?? "",
  };

  return (
    <>
      <div className={styles.viewForm}>
        <Text strong color="black" className={styles.sectionTitle}>
          {locale.orderDetails}
        </Text>
        <ViewRow
          label={locale.status}
          value={
            <Pill
              className={styles.status}
              cheddar={
                orderSummary?.status === OrderStatus.ForRevision ||
                orderSummary?.status === OrderStatus.Pending
              }
              grass={orderSummary?.status === OrderStatus.Delivered}
              cement={orderSummary?.status === OrderStatus.Canceled}
              deepRed={orderSummary?.status === OrderStatus.Rejected}
              sky={orderSummary?.status === OrderStatus.Processing}
              purple={orderSummary?.status === OrderStatus.ForDelivery}
              mainlyBlue={orderSummary?.status === OrderStatus.ForApproval}
            >
              {prettifyOrderStatus(orderSummary?.status)}
            </Pill>
          }
          loading={loading}
        />
        <ViewRow
          label={locale.stationCode}
          value={orderSummary?.stationCode ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.stationName}
          value={orderSummary?.station?.name ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.stationType}
          value={orderSummary?.station?.type ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.orderRecipient}
          value={prettifyDeliveryRecipient(orderSummary?.recipient)}
          loading={loading}
        />
        <ViewRow
          label={locale.preferredDeliveryDateTime}
          value={dateTimeFormat(orderSummary?.preferredDeliveryDate) ?? ""}
          loading={loading}
        />
        <ViewRow
          label={locale.purchaseOrderNumber}
          value={orderSummary?.purchaseOrderNumber ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.salesOrderNumber}
          value={orderSummary?.salesOrderNumber ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.productType}
          value={prettifyProductType(orderSummary?.productType) ?? "-"}
          loading={loading}
        />
        <ViewRow
          label={locale.stationLevelRemarks}
          value={orderSummary?.remarks ?? "-"}
          loading={loading}
        />
        <br />
        <br />
        <Intro title={locale.itemOrder} />
      </div>
      <DataTableV2
        {...table}
        size="small"
        hidePagination
        hideNoSearchResultRender
        tableVersion="v2"
        striped={true}
        className={styles.tableContainerView}
      />
      {orderSummary?.remarksHo === "Others" &&
        (orderSummary?.status === OrderStatus.Rejected ||
          orderSummary?.status === OrderStatus.ForRevision) && (
          <>
            <div className={styles.viewForm}>
              <ViewRow
                label={locale.headOfficeRemarks}
                value={orderSummary?.remarksHo}
                loading={loading}
              />
              <br />
            </div>
            <div className={styles.viewRemarks}>
              <div
                className={
                  orderSummary?.status === OrderStatus.Rejected
                    ? styles.remarksForRejected
                    : styles.remarksForRevision
                }
              >
                <div className={styles.rejectHeader}>
                  {orderSummary?.status === OrderStatus.Rejected ? (
                    <>
                      <WarningAmberIcon className={styles.warningIcon} />
                      <div className={styles.content}>
                        <Text strong color="red" style={{ fontWeight: "bold" }}>
                          {locale.rejectReason}
                        </Text>
                        <Text label>{`(${formatDate(orderSummary?.updatedAt, DateTime.H)})`}</Text>
                      </div>
                    </>
                  ) : (
                    <>
                      <ModeEditIcon className={styles.editIcon} />
                      <div className={styles.content}>
                        <Text strong color="red" style={{ fontWeight: "bold" }}>
                          {locale.revisionRequestReason}
                        </Text>
                        <Text label>{`(${formatDate(orderSummary?.updatedAt, DateTime.H)})`}</Text>
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.rejectDetails}>
                  <Text color="black">
                    {orderSummary?.status === OrderStatus.Rejected
                      ? "Incomplete Details"
                      : "Wrong Quantity Entered"}
                  </Text>
                </div>

                <div className={styles.rejectRemarks}>
                  <Text strong color="black">
                    {locale.remarks}
                  </Text>
                  <Text color="black">{orderSummary?.remarksHo || "-"}</Text>
                </div>
              </div>
            </div>
          </>
        )}
      <ViewRow label={locale.remarksHo} value={orderSummary?.remarksHo ?? "-"} loading={loading} />
      {(orderSummary?.status === OrderStatus.Rejected ||
        orderSummary?.status === OrderStatus.ForRevision) && (
        <StatusCard status={orderSummary?.status} {...statusCardInfo} />
      )}
    </>
  );
};

export default ViewOrderSummary;
